import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { OrgContext } from "./PortalLayout";
import { listQuotations, getQuotation, decideQuotation } from "../../services/portalService";
import { errorToast, successToast } from "../Toasts/Toasts";
import QuotationPDF from "./QuotationPDF";
import { statusClass, fmtDate, inr } from "./PortalRequirements";

// Quotations: each is a frozen version. Approve, ask for a revision, or
// reject - roles permitting - and download the PDF.

const CAN_DECIDE = (user, org) => ["CUST_ADMIN", "CUST_APPROVER"].includes(user?.role) || (user?.role === "CUST_MANAGER" && org?.settings?.approvalRule === "manager");

export const QuotationList = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { listQuotations().then(setRows).catch(() => setRows([])); }, []);
  return (
    <>
      <h5 className="mb-2">Quotations</h5>
      <div className="portal-card portal-list">
        {rows === null ? <div className="portal-empty">Loading…</div> : rows.length ? rows.map((v) => (
          <Link key={v.id} to={`/portal/quotations/${v.id}`} className="item d-block text-decoration-none text-reset">
            <div className="d-flex justify-content-between">
              <span className="fw-semibold">{v.quoteNumber}</span>
              <span className={`status-pill ${statusClass(v.status)}`}>{v.status}</span>
            </div>
            <div className="small text-muted">{v.requirement?.number} · {v.lineCount} items · {inr(v.totals.grand)} incl. GST</div>
            <div className="small text-muted">{fmtDate(v.quoteDate)} · valid till {fmtDate(v.validTill)}</div>
          </Link>
        )) : <div className="portal-empty">No quotations yet.</div>}
      </div>
    </>
  );
};

export const QuotationDetail = () => {
  const { id } = useParams();
  const { org, user } = useContext(OrgContext);
  const [v, setV] = useState(null);
  const [modal, setModal] = useState(null); // "revise" | "reject"
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => getQuotation(id).then(setV).catch((e) => errorToast(e?.message || "Not found"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (decision) => {
    setBusy(true);
    try {
      const r = await decideQuotation(id, decision, note);
      if (r?.success) {
        successToast(decision === "approve" ? `Approved — order ${r.data.order.orderNumber} created` : decision === "revise" ? "Revision requested" : "Rejected");
        setModal(null); setNote(""); load();
      } else errorToast(r?.message);
    } catch (err) { errorToast(err?.message || "Could not save"); } finally { setBusy(false); }
  };

  if (!v) return <div className="portal-empty">Loading…</div>;
  const slabs = Object.entries(v.totals.gstBySlab || {}).filter(([k]) => Number(k) > 0).sort(([a], [b]) => Number(a) - Number(b));
  const siteName = org?.sites?.find((st) => st.id === v.requirement?.siteId)?.name;
  const canDecide = CAN_DECIDE(user, org) && v.status === "Issued";
  const expired = v.status === "Issued" && new Date(v.validTill) < new Date(new Date().toDateString());

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h5 className="mb-0">{v.quoteNumber}</h5>
          <div className="small text-muted">For <Link to={`/portal/requirements/${v.requirement?.number}`}>{v.requirement?.number}</Link>{siteName ? ` · ${siteName}` : ""}</div>
          <div className="small text-muted">{fmtDate(v.quoteDate)} · valid till {fmtDate(v.validTill)}{expired ? " (expired — ask for a revision)" : ""}</div>
        </div>
        <span className={`status-pill ${statusClass(v.status)}`}>{v.status}</span>
      </div>

      {v.order && <Link to={`/portal/orders/${v.order.id}`} className="portal-action">Order {v.order.orderNumber} · {v.order.status}</Link>}

      <div className="portal-card">
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead><tr><th>Item</th><th className="text-end">Qty</th><th className="text-end">Rate</th><th className="text-end">Amount</th><th className="text-end">GST</th></tr></thead>
            <tbody>
              {v.lines.map((l, i) => (
                <tr key={l.id || i}>
                  <td>{l.item}{l.size ? <div className="small text-muted">{l.size}{l.hsnCode ? ` · HSN ${l.hsnCode}` : ""}</div> : l.hsnCode ? <div className="small text-muted">HSN {l.hsnCode}</div> : null}</td>
                  <td className="text-end">{l.quantity ?? ""}</td>
                  <td className="text-end">{inr(l.rate)}</td>
                  <td className="text-end">{inr(l.amount)}</td>
                  <td className="text-end small">{l.gstPct === null ? "" : `${l.gstPct}%`}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr><td colSpan={3} className="text-end">Taxable value</td><td className="text-end" colSpan={2}>{inr(v.totals.taxable)}</td></tr>
              {slabs.map(([slab, amt]) => <tr key={slab}><td colSpan={3} className="text-end">GST @ {slab}%</td><td className="text-end" colSpan={2}>{inr(amt)}</td></tr>)}
              <tr className="fw-bold"><td colSpan={3} className="text-end">Grand total</td><td className="text-end" colSpan={2}>{inr(v.totals.grand)}</td></tr>
            </tfoot>
          </table>
        </div>
        <div className="small text-muted mt-2">{v.terms?.paymentNote}</div>
        {v.decisionNote && <div className="small mt-2"><b>{v.status}:</b> {v.decisionNote}</div>}
      </div>

      <div className="d-grid gap-2 mb-3">
        <PDFDownloadLink document={<QuotationPDF version={v} orgName={org?.name || ""} siteName={siteName} />} fileName={`${v.quoteNumber}.pdf`} className="btn btn-outline-success">
          {({ loading }) => (loading ? "Preparing PDF…" : "Download PDF")}
        </PDFDownloadLink>
        {canDecide && (
          <>
            <Button variant="success" disabled={busy} onClick={() => decide("approve")}>Approve quotation</Button>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" className="flex-fill" onClick={() => setModal("revise")}>Request revision</Button>
              <Button variant="outline-danger" className="flex-fill" onClick={() => setModal("reject")}>Reject</Button>
            </div>
          </>
        )}
        {!canDecide && v.status === "Issued" && <div className="small text-muted text-center">Only an approver or admin of your organization can decide on this quotation.</div>}
      </div>

      <Modal show={!!modal} onHide={() => setModal(null)}>
        <Modal.Header closeButton><Modal.Title>{modal === "revise" ? "Request a revision" : "Reject quotation"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{modal === "revise" ? "What should change?" : "Reason (optional)"}</Form.Label>
            <Form.Control as="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={modal === "revise" ? "success" : "danger"} disabled={busy || (modal === "revise" && !note.trim())} onClick={() => decide(modal)}>{modal === "revise" ? "Send request" : "Reject"}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
